import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

interface NavbarProps {
  isScrolled: boolean;
}

const Navbar = ({ isScrolled }: NavbarProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAboutDropdownOpen, setIsAboutDropdownOpen] = useState(false);
  const [isMinistriesDropdownOpen, setIsMinistriesDropdownOpen] = useState(false);
  const navigate = useNavigate();

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setIsMobileMenuOpen(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    setIsMobileMenuOpen(false);
  };

  const handleNavigation = (link: typeof navLinks[0]) => {
    if (link.path) {
      navigate(link.path);
      setIsMobileMenuOpen(false);
    } else if (link.id) {
      if (link.id === 'home') {
        scrollToTop();
      } else {
        scrollToSection(link.id);
      }
    }
  };

  const navLinks = [
    { name: 'Home', id: 'home' },
    { 
      name: 'About', 
      hasDropdown: true,
      dropdownItems: [
        { name: 'Our Beliefs', path: '/beliefs' },
        { name: 'Core Values', path: '/core-values' }
      ]
    },
    { 
      name: 'Ministries', 
      hasDropdown: true,
      dropdownItems: [
        { name: 'Men Ministry', path: '/ministries/men' },
        { name: 'Women Ministry', path: '/ministries/women' },
        { name: 'Youth Ministry', path: '/ministries/youth' },
        { name: 'Children Ministry', path: '/ministries/children' },
        { name: 'Evangelism Ministry', path: '/ministries/evangelism' }
      ]
    },
    { name: 'Resources', path: '/resources' },
    { name: 'Blog', id: 'blog' },
    { name: 'Contact', id: 'contact' },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-[#0842A0]'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <button
              onClick={() => navigate('/')}
              className="flex items-center cursor-pointer bg-transparent border-none p-0"
            >
              <img 
                src="https://static.readdy.ai/image/968c370221603733d312598be0660945/76f1e59cb0ede40759a88b475c1da3a0.png" 
                alt="COP Mansfield Logo" 
                className="h-12 w-auto"
                style={{ mixBlendMode: isScrolled ? 'multiply' : 'screen' }}
              />
            </button>

            <div className="hidden lg:flex items-center space-x-8">
              {navLinks.map((link) => (
                link.hasDropdown ? (
                  <div 
                    key={link.name}
                    className="relative"
                    onMouseEnter={() => {
                      if (link.name === 'About') {
                        setIsAboutDropdownOpen(true);
                      } else if (link.name === 'Ministries') {
                        setIsMinistriesDropdownOpen(true);
                      }
                    }}
                    onMouseLeave={() => {
                      if (link.name === 'About') {
                        setIsAboutDropdownOpen(false);
                      } else if (link.name === 'Ministries') {
                        setIsMinistriesDropdownOpen(false);
                      }
                    }}
                  >
                    <button
                      className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap cursor-pointer flex items-center gap-1 ${
                        isScrolled
                          ? 'text-[#0842A0] hover:text-[#0842A0]/80'
                          : 'text-white hover:text-[#FFD700]'
                      }`}
                    >
                      {link.name}
                      <i className="ri-arrow-down-s-line text-base"></i>
                    </button>
                    <AnimatePresence>
                      {((link.name === 'About' && isAboutDropdownOpen) || 
                        (link.name === 'Ministries' && isMinistriesDropdownOpen)) && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-lg overflow-hidden border border-gray-100"
                        >
                          {link.dropdownItems?.map((item) => (
                            <button
                              key={item.name}
                              onClick={() => {
                                navigate(item.path);
                                setIsAboutDropdownOpen(false);
                                setIsMinistriesDropdownOpen(false);
                              }}
                              className="w-full text-left px-4 py-3 text-sm font-medium text-gray-700 hover:bg-[#FFD700]/10 hover:text-[#0842A0] transition-colors whitespace-nowrap cursor-pointer"
                            >
                              {item.name}
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <button
                    key={link.name}
                    onClick={() => handleNavigation(link)}
                    className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                      isScrolled
                        ? 'text-[#0842A0] hover:text-[#0842A0]/80'
                        : 'text-white hover:text-[#FFD700]'
                    }`}
                  >
                    {link.name}
                  </button>
                )
              ))}
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`ri-menu-line text-2xl ${isScrolled ? 'text-[#0842A0]' : 'text-white'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="fixed top-0 right-0 bottom-0 w-64 bg-white shadow-xl z-50 lg:hidden"
            >
              <div className="flex justify-end p-6">
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 cursor-pointer"
                  aria-label="Close menu"
                >
                  <i className="ri-close-line text-2xl text-[#0842A0]"></i>
                </button>
              </div>
              <div className="flex flex-col space-y-4 px-6">
                {navLinks.map((link) => (
                  link.hasDropdown ? (
                    <div key={link.name}>
                      <div className="text-left text-base font-medium text-[#0842A0] mb-2">
                        {link.name}
                      </div>
                      <div className="pl-4 space-y-2">
                        {link.dropdownItems?.map((item) => (
                          <button
                            key={item.name}
                            onClick={() => {
                              navigate(item.path);
                              setIsMobileMenuOpen(false);
                            }}
                            className="w-full text-left text-sm font-medium text-gray-600 hover:text-[#0842A0] transition-colors whitespace-nowrap cursor-pointer"
                          >
                            {item.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <button
                      key={link.name}
                      onClick={() => handleNavigation(link)}
                      className="text-left text-base font-medium text-gray-700 hover:text-[#0842A0] transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {link.name}
                    </button>
                  )
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
